/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure decision logic for the launch-time build-version gate. The hook
 * (useForceUpdateGate) reads the installed native build number + the live
 * RTDB `/config/global` doc and feeds them here; isolating the logic keeps it
 * testable without Capacitor or Firebase plumbing. Mirrors the SOS
 * kill-switch pattern (evaluateSosEnabled).
 *
 * Fail-open is the contract: any uncertainty — unparseable build, missing or
 * malformed config, NaN threshold — yields `show: false` so a config typo or
 * a read hiccup can never trap a user out of the app.
 */

/** App Store deep link for the Update CTA (id from the live listing). */
export const APP_STORE_UPDATE_URL = "itms-apps://apps.apple.com/app/id6761281097";

export interface GlobalConfig {
  minSupportedBuild?: unknown;
  forceUpdate?: unknown;
  updateMessage?: unknown;
}

export interface ForceUpdateDecision {
  /** Whether the update modal should render at all. */
  show: boolean;
  /** When shown: false = hard-block (non-dismissible), true = soft-nudge. */
  dismissible: boolean;
  /** Optional custom copy from config; undefined falls back to default UI text. */
  message?: string;
}

const NO_MODAL: ForceUpdateDecision = { show: false, dismissible: false };

/**
 * Parse a native build number (Capacitor `App.getInfo().build` is a string,
 * e.g. "164") into an integer. Returns null for anything that isn't a clean,
 * finite, non-negative integer — callers treat null as "unknown → fail-open".
 */
export function parseBuildNumber(raw: unknown): number | null {
  if (typeof raw === "number") {
    return Number.isInteger(raw) && raw >= 0 ? raw : null;
  }
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  // Reject empty and anything that isn't pure digits (avoids parseInt's
  // lenient "164abc" → 164 and "" → NaN edge cases).
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number.parseInt(trimmed, 10);
  return Number.isInteger(n) ? n : null;
}

/**
 * Decide whether to show the force-update modal.
 *
 * @param installedBuild the parsed installed build number, or null if it
 *   couldn't be determined (→ fail-open, no modal).
 * @param config the raw `/config/global` value from RTDB (any shape).
 */
export function evaluateForceUpdate(
  installedBuild: number | null,
  config: unknown,
): ForceUpdateDecision {
  // Unknown installed build → never gate (can't make a safe comparison).
  if (installedBuild === null) return NO_MODAL;

  if (config === null || config === undefined || typeof config !== "object") {
    return NO_MODAL;
  }

  const { minSupportedBuild, forceUpdate, updateMessage } = config as GlobalConfig;

  // Threshold must be a finite number. Missing / NaN / non-number → fail-open.
  if (typeof minSupportedBuild !== "number" || !Number.isFinite(minSupportedBuild)) {
    return NO_MODAL;
  }

  // At or above threshold → nothing to do.
  if (installedBuild >= minSupportedBuild) return NO_MODAL;

  // Below threshold → show. Hard-block only when forceUpdate is exactly true.
  return {
    show: true,
    dismissible: forceUpdate !== true,
    message: typeof updateMessage === "string" && updateMessage.trim() ? updateMessage : undefined,
  };
}
